import React, { useEffect, useState } from "react";
import { api, formatError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { TrendingUp, DollarSign } from "lucide-react";

const COLORS = ["#4A0E17", "#E65100", "#D4AF37", "#8C6D3B", "#2F7D32", "#1B5E20"];

export function RevenueTab() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/revenue").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="p-10 text-[#63534E]">Loading…</div>;

  const maxGross = Math.max(1, ...data.rows.map((r) => r.gross));

  return (
    <div data-testid="revenue-tab">
      <div className="grid md:grid-cols-3 gap-5">
        <div className="bg-gradient-to-br from-[#4A0E17] to-[#36080F] text-[#FAF6F0] rounded-2xl p-6 relative overflow-hidden">
          <div className="absolute -right-4 -top-6 font-devanagari text-[120px] leading-none text-[#D4AF37] opacity-10">ॐ</div>
          <TrendingUp className="w-6 h-6 text-[#D4AF37]" />
          <div className="overline text-[#D4AF37] mt-3">Gross transactions</div>
          <div className="font-mono text-3xl mt-1 font-semibold">${data.totals.gross.toFixed(2)}</div>
        </div>
        <div className="bg-[#FFFDF9] sacred-border rounded-2xl p-6">
          <DollarSign className="w-6 h-6 text-[#E65100]" />
          <div className="overline mt-3">Platform success fees</div>
          <div className="font-mono text-3xl mt-1 font-semibold text-[#E65100]">${data.totals.platform_fee.toFixed(2)}</div>
          <div className="text-xs text-[#63534E] mt-1">Our share across all streams</div>
        </div>
        <div className="bg-[#FFFDF9] sacred-border rounded-2xl p-6">
          <div className="overline">Paid to beneficiaries</div>
          <div className="font-mono text-3xl mt-1 font-semibold text-[#2F7D32]">${data.totals.to_beneficiaries.toFixed(2)}</div>
          <div className="text-xs text-[#63534E] mt-1">To the temple, teachers, and travel desk</div>
        </div>
      </div>

      <div className="mt-10 bg-[#FFFDF9] sacred-border rounded-2xl p-7">
        <h3 className="font-serif text-2xl text-[#4A0E17]">Revenue by stream</h3>
        <p className="text-sm text-[#63534E] mt-2">Where our platform fees are coming from — scale up the categories with high ratios.</p>

        <div className="mt-7 space-y-5">
          {data.rows.length === 0 && <div className="text-center text-[#63534E] py-8">No paid transactions yet — once a devotee completes a checkout, this chart will fill up.</div>}
          {data.rows.sort((a, b) => b.gross - a.gross).map((r, i) => {
            const pct = Math.round((r.gross / maxGross) * 100);
            return (
              <div key={r.type} data-testid={`revenue-row-${r.type}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                    <span className="font-semibold text-[#4A0E17] capitalize">{r.type.replace(/_/g, " ")}</span>
                    <span className="text-xs text-[#63534E]">· {r.count} tx · {r.fee_rate_percent}% fee</span>
                  </div>
                  <div className="font-mono text-sm text-[#1F1412]">
                    <span className="text-[#63534E]">gross</span> ${r.gross.toFixed(2)}
                    <span className="mx-2">·</span>
                    <span className="text-[#E65100] font-semibold">fee ${r.platform_fee.toFixed(2)}</span>
                  </div>
                </div>
                <div className="h-3 bg-[#F3E5AB]/40 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: COLORS[i % COLORS.length] }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function SuppliersTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/suppliers").then((r) => setItems(r.data.items));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try { await api.put(`/admin/suppliers/${id}/status`, { status }); toast.success(status); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const badge = (s) => s === "approved" ? "bg-green-100 text-green-800"
    : s === "pending" ? "bg-amber-100 text-amber-800"
    : s === "onboarded" ? "bg-blue-100 text-blue-800" : "bg-red-50 text-red-800";

  return (
    <div data-testid="suppliers-tab" className="bg-[#FFFDF9] sacred-border rounded-2xl overflow-hidden">
      <div className="px-6 py-4 border-b border-[#D4AF37]/30">
        <div className="font-serif text-xl text-[#4A0E17]">Supplier applications</div>
        <div className="text-sm text-[#63534E] mt-1">{items.length} total · 15% success fee</div>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-[#4A0E17] text-[#F3E5AB]"><tr>
          <th className="text-left px-4 py-3">Business</th>
          <th className="text-left px-4 py-3">Contact</th>
          <th className="text-left px-4 py-3">Category</th>
          <th className="text-left px-4 py-3">Country</th>
          <th className="text-left px-4 py-3">Status</th>
          <th className="text-left px-4 py-3">Actions</th>
        </tr></thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-[#63534E]">No applications yet.</td></tr>}
          {items.map((s) => (
            <tr key={s.id} className="border-t border-[#D4AF37]/20">
              <td className="px-4 py-3"><div className="font-semibold">{s.business_name}</div><div className="text-xs text-[#63534E]">{s.website}</div></td>
              <td className="px-4 py-3">{s.contact_name}<br/><span className="text-xs text-[#63534E] font-mono">{s.email}</span></td>
              <td className="px-4 py-3 capitalize">{s.category}</td>
              <td className="px-4 py-3">{s.country}</td>
              <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs ${badge(s.status)}`}>{s.status}</span></td>
              <td className="px-4 py-3">
                <div className="flex gap-2">
                  {s.status === "pending" && (<>
                    <Button data-testid={`sup-approve-${s.id}`} onClick={() => setStatus(s.id, "approved")} className="h-8 text-xs bg-[#2F7D32] hover:bg-[#1B5E20] rounded-full px-3">Approve</Button>
                    <Button data-testid={`sup-reject-${s.id}`} onClick={() => setStatus(s.id, "rejected")} variant="outline" className="h-8 text-xs rounded-full px-3">Reject</Button>
                  </>)}
                  {s.status === "approved" && (<Button data-testid={`sup-onboard-${s.id}`} onClick={() => setStatus(s.id, "onboarded")} className="h-8 text-xs bg-[#E65100] hover:bg-[#BF360C] rounded-full px-3">Mark onboarded</Button>)}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
